// src/pages/OrganizationSettingsForm.js

import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';

const OrganizationSettingsForm = () => {
  const api = useApi();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    email: '',
    phone: '',
    logoUrl: '',
    isActive: true, // Default to true
  });
  const [selectedLogoFile, setSelectedLogoFile] = useState(null); // For file upload
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [organizationId, setOrganizationId] = useState(null); // To store the ID of the organization record
  const [initialLogoUrl, setInitialLogoUrl] = useState(''); // Store initial logo URL for preview reset
  // Fetch organization data on component mount
  const fetchOrganizationData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Assuming a GET endpoint that returns the *single* organization record
      // Or you might fetch by a known ID if you manage multiple organizations
      const {data:response} = await api.get('organization'); // e.g., GET /api/organization
      if (response && response._id) {
        const fetchedLogoUrl = response.logo || '';
        setFormData({
          name: response.name || '',
          code: response.code || '',
          address: response.address || '',
          email: response.email || '',
          phone: response.phone || '',
          logoUrl: fetchedLogoUrl,
          isActive: typeof response.isActive === 'boolean' ? response.isActive : true,
        });
        setOrganizationId(response._id); // Store the ID for PUT requests
        setInitialLogoUrl(fetchedLogoUrl); // Store initial logo URL
      } else {
        // If no organization record exists, initialize with defaults
        console.warn("No organization data found. Initializing new form.");
        setFormData({
          name: '', code: '', address: '', email: '', phone: '', logoUrl: '', isActive: true
        });
        setOrganizationId(null); // No existing ID
        setInitialLogoUrl(''); // No initial logo URL
      }
      setSelectedLogoFile(null); // Reset file selection on fetch
    } catch (err) {
      setError(err.message || 'Failed to fetch organization data.');
      console.error('Error fetching organization data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchOrganizationData();
  }, [fetchOrganizationData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        // Set the base64 string to formData.logoUrl
        setFormData(prev => ({ ...prev, logoUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedLogoFile(null);
      // Revert to the initial logo URL if the file input is cleared
      setFormData(prev => ({ ...prev, logoUrl: initialLogoUrl }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    // Basic validation
    if (!formData.name.trim() || !formData.code.trim()) {
      setError("Organization Name and Code are required.");
      setIsSaving(false);
      return;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address.");
      setIsSaving(false);
      return;
    }

    // formData.logoUrl will contain the base64 string if a new file was selected,
    // or the existing URL if no new file was selected.
    // The backend needs to handle this distinction.
    const submissionPayload = { ...formData };

    try {
      if (organizationId) {
        // Update existing organization
        await api.put(`organization/${organizationId}`, submissionPayload);
        alert("Organization profile updated successfully!");
      } else {
        // Create new organization (assuming your backend handles creation if none exists)
        const createdOrg = await api.post('organization', submissionData); // Assumes api.post returns the created object
        if (createdOrg && createdOrg._id) {
          setOrganizationId(createdOrg._id); // Save the new ID
        }
        alert("Organization profile created successfully!");
      }
      // Re-fetch data to ensure UI reflects any backend processing/defaults
      fetchOrganizationData();
    } catch (err) {
      setError(err.message || "Failed to save organization data.");
      console.error("Organization form submission error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && !isSaving) { // Only show error if not currently trying to save again
    return (
      <ErrorMessage message={error}>
        <button onClick={fetchOrganizationData} className="mt-2 bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600">
          Retry Loading
        </button>
      </ErrorMessage>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-md">
      {error && <ErrorMessage message={error} />}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Organization Name</label>
        <input
          type="text"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleInputChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div>
        <label htmlFor="code" className="block text-sm font-medium text-gray-700">Organization Code</label>
        <input
          type="text"
          name="code"
          id="code"
          value={formData.code}
          onChange={handleInputChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div>
        <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address (Optional)</label>
        <textarea
          name="address"
          id="address"
          value={formData.address}
          onChange={handleInputChange}
          rows="3"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
        ></textarea>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email (Optional)</label>
        <input
          type="email"
          name="email"
          id="email"
          value={formData.email}
          onChange={handleInputChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone (Optional)</label>
        <input
          type="tel"
          name="phone"
          id="phone"
          value={formData.phone}
          onChange={handleInputChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div className="mt-4"> {/* Adjusted margin if needed */}
        <label htmlFor="logoFile" className="block text-sm font-medium text-gray-700">Upload Logo File</label>
        <input
          type="file"
          name="logoFile"
          id="logoFile"
          accept="image/png, image/jpeg, image/gif, image/svg+xml"
          onChange={handleFileChange}
          className="mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer bg-gray-50 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 p-2"
        />
        {selectedLogoFile && (
          <p className="mt-1 text-xs text-gray-600">Selected file: {selectedLogoFile.name}</p>
        )}
        {formData.logoUrl && (
          <div className="mt-2">
            <p className="text-sm font-medium text-gray-700">Logo Preview:</p>
            <img
              src={formData.logoUrl}
              alt="Logo Preview"
              className="mt-1 max-h-32 max-w-xs border border-gray-300 rounded object-contain"
            />
          </div>
        )}
      </div>
      <div className="flex items-center">
        <input
          type="checkbox"
          name="isActive"
          id="isActive"
          checked={formData.isActive}
          onChange={handleInputChange}
          disabled // Add the disabled attribute here
          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
        />
        <label htmlFor="isActive" className="ml-2 block text-sm font-medium text-gray-900">
          Is Organization Active?
        </label>
      </div>

      <div className="flex justify-end mt-6">
        <button
          type="submit"
          disabled={isSaving}
          className="px-6 py-2 border border-transparent rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
        >
          {isSaving ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </form>
  );
};

export default OrganizationSettingsForm;
